import React, { useEffect, useState, useRef, memo, useMemo, useDeferredValue, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom";
import { flujoCompraTerminado, terminarFlujoCompra, empezarCompra, flujoCompraReanudado, leerBorradorModificar, guardarBorradorModificar, borrarBorradorModificar, registrarEntradaHistorial } from "../../utils/flujoCompra";
import { Virtuoso } from "react-virtuoso";
import { hoyMadrid } from "../../utils/fecha";

// Normalizador de clave de categoría (quita acentos, minúsculas, trim)
const nrmKey = (s) => String(s || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

// Mapa de posiciones del sprite para cada categoría (añade más según necesites)
const ICON_POSITIONS = {
  [nrmKey("Aceite, especias y salsas")]: "-84px -28px",
  [nrmKey("Agua y refrescos")]: "-308px -28px",
  [nrmKey("Aperitivos")]: "-252px -28px",
  [nrmKey("Arroz, legumbres y pasta")]: "-224px -28px",
  [nrmKey("Azúcar, caramelos y chocolate")]: "-112px 0",
  [nrmKey("Bodega")]: "0 0",
  [nrmKey("Cacao, café e infusiones")]: "-168px 0",
  [nrmKey("Carne")]: "-28px -28px",
  [nrmKey("Cereales y galletas")]: "-140px 0",
  [nrmKey("Charcutería y quesos")]: "-84px 0",
  [nrmKey("Congelados")]: "-224px 0",
  [nrmKey("Conservas, caldos y cremas")]: "-196px -28px",
  [nrmKey("Cuidado facial y corporal")]: "-280px -28px",
  [nrmKey("Fruta y verdura")]: "-252px 0",
  [nrmKey("Huevos, leche y mantequilla")]: "-56px -28px",
  [nrmKey("Limpieza y hogar")]: "-308px 0",
  [nrmKey("Marisco y pescado")]: "-196px 0",
  [nrmKey("Panadería y pastelería")]: "-56px 0",
  [nrmKey("Pizzas y platos preparados")]: "-168px -28px",
  [nrmKey("Postres y yogures")]: "-336px -28px",
  [nrmKey("Zumos")]: "-336px 0",
};

const normalizar = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const tokensMatch = (nameTokens, searchTokens) => {
  if (!searchTokens.length) return true;
  if (!nameTokens || !nameTokens.length) return false;
  return searchTokens.every((st) => nameTokens.some((nt) => nt.startsWith(st)));
};

// Componente Producto individual
// Compra NUEVA de un piso que ya tiene un pedido en estado "pedido": no se deja
// seguir (se vuelve a la lista de pisos, que ya no lo muestra), se llegue desde
// donde se llegue (atrás, recarga, otro móvil…). Lo comprueba el servidor.
const avisoPedidoPendiente = (abreviatura, d) =>
  `${abreviatura ? `El piso ${abreviatura}` : "Este piso"} ya tiene un pedido pendiente (compra #${d.id}` +
  `${d.precio_total != null ? `, ${Number(d.precio_total).toFixed(2).replace(".", ",")} €` : ""}). ` +
  "Para cambiarlo usa «Modificar compras».";

const ProductoRow = memo(({ producto, cantidadSeleccionada, modoNoHay, seleccionarProducto, aumentarCantidad, disminuirCantidad, blocked }) => (
  <div className={`producto-row ${blocked ? 'producto-blocked' : ''}`} style={{ textAlign: "center", marginBottom: "20px", position: 'relative' }}>
    <img src={producto.imagen} alt={producto.nombre} className="producto-imagen" loading="lazy" decoding="async" />
    <div className="producto-info">
      <strong>{producto.nombre}</strong>
      <div>Cantidad: {producto.cantidad}kg</div>
      <div className="price">
        Precio: {producto.precio.toFixed(2)} € {" "}
        <span className="precioKg">
          ({producto["precio/kg"].toFixed(2)} €/kg)
        </span>
      </div>
    </div>
    <div className="cantidadControls" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {modoNoHay ? (
        blocked ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="blocked-badge">Producto ya en la compra</div>
          </div>
        ) : (
          <button
            className="cantidadBtn"
            onClick={() => seleccionarProducto(producto)}
            title={'Seleccionar producto'}
          >
            Seleccionar producto
          </button>
        )
      ) : (
        <>
          <button className="cantidadBtn" onClick={() => disminuirCantidad(producto.id)} disabled={cantidadSeleccionada === 0 || !!blocked}>–</button>
          <div className="cantidadNum">{cantidadSeleccionada}</div>
          <button className="cantidadBtn" onClick={() => aumentarCantidad(producto.id)} disabled={!!blocked}>+</button>
        </>
      )}
    </div>
  </div>
));

// Componente Subcategoría
const Subcategoria = memo(({ sub, isPremium, openSubcategoria, setOpenSubcategoria, carrito, modoNoHay, seleccionarProducto, aumentarCantidad, disminuirCantidad, busquedaFiltrada, subRef, blockedIds }) => {
  const handleSubClick = () => {
    const nuevaSub = openSubcategoria === sub.subcategoria ? null : sub.subcategoria;
    setOpenSubcategoria(nuevaSub);
    if (nuevaSub) {
      // Deferimos el scroll hasta el próximo frame y revalidamos la ref por si aún no existe o cambió
      setTimeout(() => {
        const el = subRef?.current;
        if (!el) return;
        requestAnimationFrame(() => {
          const node = subRef?.current;
          if (!node) return;
          const rect = node.getBoundingClientRect?.();
          if (!rect) return;
          const top = rect.top + window.scrollY - 90;
          window.scrollTo({ top, behavior: "smooth" });
        });
      }, 100);
    }
  };

  if (!sub.productos || sub.productos.length === 0) return null;
  const isOpen = busquedaFiltrada.length > 0 || openSubcategoria === sub.subcategoria;

  return (
    <div ref={subRef} className="subcategory" style={{ backgroundColor: isPremium ? "#fff176" : "#eefdea", padding: "3px 8px", borderRadius: "5px", marginBottom: "5px" }}>
      <summary style={{ cursor: "pointer", fontSize: 24, fontWeight: 'bold', marginBottom: 15 }} onClick={handleSubClick}>
        {sub.subcategoria}
      </summary>
      {isOpen && (
        (sub.productos?.length || 0) > 10 ? (
          <div style={{ height: 480 }}>
            <Virtuoso
              data={sub.productos}
              itemContent={(index, p) => {
                const blocked = blockedIds && blockedIds.has(String(p.id));
                return (
                  <ProductoRow
                    key={p.id || index}
                    producto={p}
                    cantidadSeleccionada={carrito[p.id] || 0}
                    modoNoHay={modoNoHay}
                    seleccionarProducto={seleccionarProducto}
                    aumentarCantidad={aumentarCantidad}
                    disminuirCantidad={disminuirCantidad}
                    blocked={blocked}
                  />
                );
              }}
            />
          </div>
        ) : (
          sub.productos.map((p) => {
            const blocked = blockedIds && blockedIds.has(String(p.id));
            return (
              <ProductoRow
                key={p.id}
                producto={p}
                cantidadSeleccionada={carrito[p.id] || 0}
                modoNoHay={modoNoHay}
                seleccionarProducto={seleccionarProducto}
                aumentarCantidad={aumentarCantidad}
                disminuirCantidad={disminuirCantidad}
                blocked={blocked}
              />
            );
          })
        )
      )}
    </div>
  );
});

// Componente Categoría
const Categoria = memo(({ cat, isPremium, openCategoria, setOpenCategoria, openSubcategoria, setOpenSubcategoria, carrito, modoNoHay, seleccionarProducto, aumentarCantidad, disminuirCantidad, busquedaFiltrada, subcategoryRefs, blockedIds }) => {
  const handleCategoryClick = () => {
    const nuevaCat = openCategoria === cat.categoria ? null : cat.categoria;
    setOpenCategoria(nuevaCat);
  };

  if (!cat.subcategorias || cat.subcategorias.length === 0) return null;
  const isOpen = busquedaFiltrada.length > 0 || openCategoria === cat.categoria;

  return (
    <div className="category" style={{ backgroundColor: isPremium ? "#ffeb3b" : "#a3e29b", padding: "5px 10px", borderRadius: "5px", marginBottom: "10px" }}>
      <summary style={{ cursor: "pointer", fontSize: 24, fontWeight: 'bold', marginBottom: 15 }} onClick={handleCategoryClick}>
        <span className="cat-header" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {ICON_POSITIONS[nrmKey(cat.categoria)] && (
            <span
              className="cat-icon"
              aria-hidden
              style={{
                display: 'inline-block',
                width: 28,
                height: 28,
                backgroundImage: "url('/images/category-icons.svg')",
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto',
                backgroundPosition: ICON_POSITIONS[nrmKey(cat.categoria)],
              }}
            />
          )}
          <span>{cat.categoria}</span>
        </span>
      </summary>
      {isOpen &&
        cat.subcategorias.map((sub) => {
          if (!subcategoryRefs.current[sub.subcategoria]) {
            subcategoryRefs.current[sub.subcategoria] = React.createRef();
          }
          return (
              <Subcategoria
                key={sub.subcategoria}
                sub={sub}
                isPremium={isPremium}
                openSubcategoria={openSubcategoria}
                setOpenSubcategoria={setOpenSubcategoria}
                carrito={carrito}
                modoNoHay={modoNoHay}
                seleccionarProducto={seleccionarProducto}
                aumentarCantidad={aumentarCantidad}
                disminuirCantidad={disminuirCantidad}
                busquedaFiltrada={busquedaFiltrada}
                subRef={subcategoryRefs.current[sub.subcategoria]}
                blockedIds={blockedIds}
              />
            );
        })}
    </div>
  );
});

// El borrador de "crear compra" se guarda solo por id_piso (sin fecha), así que
// si la supervisora empieza una compra y sale sin confirmarla, ese borrador se
// queda ahí para siempre: la próxima vez que entre a ese piso (aunque sea
// semanas después, para una compra totalmente distinta) se le carga el
// carrito viejo como si fuera el actual. todayISO() permite descartar
// borradores de otro día en vez de resucitarlos sin más.
const todayISO = hoyMadrid; // fecha de Madrid (antes UTC: de 00:00 a 02:00 contaba como ayer)

const CrearCompras = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carrito, setCarrito] = useState({});
  const [abreviatura_piso, setAbreviaturaPiso] = useState("");
  const [notas, setNotas] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const deferredBusqueda = useDeferredValue(busqueda);

  // hidratación
  const [hidratado, setHidratado] = useState(false);
  const hydratedRef = useRef(false);

  const productosSeleccionadosRef = useRef([]);

  const productosFiltrados = useMemo(() => {
    if (!productos.length) return [];
    const searchTokens = normalizar(deferredBusqueda).split(" ").filter(Boolean);
    return productos
      .map((cat) => ({
        ...cat,
        subcategorias: (cat.subcategorias || [])
          .map((sub) => ({
            ...sub,
            productos: (sub.productos || []).filter((p) => tokensMatch(p._tokens, searchTokens))
          }))
          .filter((sub) => sub.productos.length > 0)
      }))
      .filter((cat) => cat.subcategorias.length > 0)
      .sort((a, b) => a.categoria === "Premium" ? 1 : b.categoria === "Premium" ? -1 : 0);
  }, [productos, deferredBusqueda]);
  const [mostrarFormularioCambio, setMostrarFormularioCambio] = useState(false);
  const [formCambio, setFormCambio] = useState({ nombre: "", cantidad: "", precio: "", unidades: "", imagen: null });
  const [modoNoHay, setModoNoHay] = useState(false);

  const [openCategoria, setOpenCategoria] = useState(null);
  const [openSubcategoria, setOpenSubcategoria] = useState(null);
  const categoryRefs = useRef({});
  const subcategoryRefs = useRef({});

  const location = useLocation();
  const navigate = useNavigate();
  const { id_piso: id_piso_param } = useParams();

  // Siempre empezar arriba del todo al entrar en la vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const {
    id,
    user_supervisora,
    id_piso: id_piso_state,
    abreviatura,
    productos: productosPrevios,
    productoSeleccionado,
    notas_supervisora,
    modoNoHay: modoNoHayState,
    origen,
    accessToken: incomingAccessToken,
    desdeModificar,
  } = location.state || {};

  // id_piso: prefer state (ya validado por React Router), fallback al param de URL (sobrevive refresh)
  const id_piso = id_piso_state || id_piso_param;

  // PROTECCIÓN "ATRÁS" TRAS CONFIRMAR. Las guardas de más abajo miran
  // performance.getEntriesByType('navigation'), que es cómo se cargó la PÁGINA,
  // no los "atrás" dentro de la app (no recargan), así que no saltaban nunca: se
  // podía volver con atrás a esta pantalla de un pedido ya confirmado, con el
  // carrito vacío, y volver a enviarlo. useNavigationType() del router sí lo
  // distingue: POP = atrás/adelante (o recarga); PUSH = entrada normal.
  const tipoNavegacion = useNavigationType();
  // Modificar: ¿se han recuperado cambios sin confirmar al entrar? ({ts}) y ¿ha tocado algo?
  const [recuperadoModificar, setRecuperadoModificar] = useState(null);
  const tocadoRef = useRef(false);
  // ¿Se llega con "atrás" a una compra que ya se confirmó? (se mira al entrar,
  // antes de que ningún efecto lo cambie)
  const finAlEntrar = useRef(tipoNavegacion === 'POP' && !modoNoHayState && flujoCompraTerminado());
  useEffect(() => {
    if (finAlEntrar.current) { terminarFlujoCompra(navigate); return; } // atrás tras confirmar → como un árbol
    if (tipoNavegacion === 'PUSH' && !modoNoHayState) empezarCompra(); // entrada normal: compra nueva
    if (desdeModificar) {
      // cambios sin confirmar de esta compra (de otra vez): se recuperan y se avisa
      const b = leerBorradorModificar(id);
      if (b && tipoNavegacion === 'PUSH') setRecuperadoModificar({ ts: b.ts });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (finAlEntrar.current || flujoCompraReanudado()) return; // ya se está saliendo del flujo
    if (!id_piso || id || desdeModificar || modoNoHayState) return; // editar una compra existente / "no hay": sin cambios
    const clave = `compraConfirmada_${id_piso}`;
    let confirmada = false;
    try { confirmada = sessionStorage.getItem(clave) === '1'; } catch (_) {}
    if (!confirmada) return;
    if (tipoNavegacion === 'POP') {
      navigate('/supervisora/pisoCrearCompras', { replace: true, state: { aviso: 'Ese pedido ya está confirmado. Para cambiarlo usa «Modificar compras».' } });
    } else if (tipoNavegacion === 'PUSH') {
      // entrada normal (p. ej. desde la lista de pisos): empieza una compra nueva de este piso
      try { sessionStorage.removeItem(clave); } catch (_) {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_piso]);

  useEffect(() => {
    // Modificar compras (id / desdeModificar) y "no hay" del comprador: no aplica
    if (finAlEntrar.current || flujoCompraReanudado() || !id_piso || id || desdeModificar || modoNoHayState || origen === 'DetalleCompra') return undefined;
    let vivo = true;
    axios.get(`/api/carrito/pedido_pendiente/${id_piso}`)
      .then((r) => {
        if (vivo && r.data?.pendiente) {
          navigate('/supervisora/pisoCrearCompras', { replace: true, state: { aviso: avisoPedidoPendiente(abreviatura, r.data) } });
        }
      })
      .catch(() => {});
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_piso]);

  // Construir set de ids bloqueados cuando venimos desde DetalleCompra
  const blockedIds = useMemo(() => {
    if (origen !== 'DetalleCompra') return new Set();
    const s = new Set();
    try {
      if (Array.isArray(productosPrevios)) {
        productosPrevios.forEach((p) => { if (p && p.id != null) s.add(String(p.id)); });
      }
      if (productoSeleccionado && productoSeleccionado.id != null) s.add(String(productoSeleccionado.id));
    } catch (e) {
      console.error('Error building blockedIds', e);
    }
    return s;
  }, [origen, productosPrevios, productoSeleccionado]);

  const anyBlocked = blockedIds && blockedIds.size > 0;

  // EARLY GUARD (refuerzo Option C): si la compra ya fue confirmada para este piso y no traemos token válido
  // (y no estamos en modo edición con un id), redirigimos antes de cargar/hidratar nada.
  useEffect(() => {
    if (!id_piso) return; // necesitamos piso
    const confirmKey = `compraConfirmada_${id_piso}`;
    const tokenKey = `crearCompras_token_${id_piso}`;
    const confirmada = sessionStorage.getItem(confirmKey) === '1';
    const storedToken = sessionStorage.getItem(tokenKey);
    const navType = performance.getEntriesByType('navigation')[0]?.type; // navigate | back_forward | reload
    // Condiciones de bloqueo:
    //  * Compra confirmada
    //  * No hay id (no edición real)
    //  * Llegamos por back/forward sin token válido
    // OJO: el chequeo de navType es imprescindible aquí (antes faltaba, a
    // diferencia del efecto gemelo de abajo): sin él, esta guarda bloqueaba
    // TAMBIÉN una entrada normal desde la lista de pisos (p.ej. para añadir
    // más productos a un pedido que ya se había confirmado antes), expulsando
    // a la supervisora a "mis compras" con un aviso de "flujo ya cerrado" sin
    // dejarle ni cargar el piso — dando la sensación de que la compra "no se
    // guardaba".
    if (confirmada && !id && navType === 'back_forward') {
      if (!incomingAccessToken || !storedToken || storedToken !== incomingAccessToken) {
        navigate('/supervisora/compras', { replace: true, state: { aviso: 'Flujo de compra ya cerrado. No se puede reabrir.' } });
      }
    }
  }, [id_piso, id, incomingAccessToken, navigate]);

  // --- CONTROL DE REENTRADA TRAS CONFIRMACIÓN (token + flags en sessionStorage) ---
  // Estrategia:
  // 1. Al entrar legítimamente (sin accessToken entrante) generamos un token y lo guardamos en sessionStorage.
  // 2. Al navegar al carrito pasamos el token por state.
  // 3. Cuando el carrito confirma, marca `compraConfirmada_<id_piso>` y elimina el token.
  // 4. Si el usuario pulsa atrás (navigation type = back_forward) y la compra ya está confirmada SIN token válido -> se redirige fuera.
  // Esto evita reabrir / rehidratar un flujo ya cerrado sin impedir una edición controlada si en el futuro agregas un modo "editar" con nuevo token.
  const [accessToken, setAccessToken] = useState(incomingAccessToken || null);
  useEffect(() => {
    // Necesitamos id_piso para construir las claves de control; si aún no está disponible salimos.
    if (!id_piso) return;
    const confirmKey = `compraConfirmada_${id_piso}`;
    const tokenKey = `crearCompras_token_${id_piso}`;

    // Si no tenemos token entrante, generamos uno nuevo (entrada legítima por flujo normal UI)
    if (!incomingAccessToken) {
      // Generar token robusto
      const nuevoToken = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(tokenKey, nuevoToken);
      setAccessToken(nuevoToken);
    } else {
      // Aseguramos persistencia del token actual mientras dure la sesión
      sessionStorage.setItem(tokenKey, incomingAccessToken);
    }

    // Detección de navegación por botón atrás / forward
    const navType = performance.getEntriesByType('navigation')[0]?.type; // back_forward | navigate | reload
    const compraConfirmada = sessionStorage.getItem(confirmKey) === '1';
    const storedToken = sessionStorage.getItem(tokenKey);

    // Escenario bloqueo:
    // - La compra ya fue confirmada
    // - El usuario llega vía back/forward del navegador
    // - No trae un token válido (o ha sido invalidado)
    // Permitir ENTRAR en modo edición si existe un id de compra (estamos editando una ya creada)
    // Solo bloqueamos reentrada "fantasma" cuando se intenta recrear SIN id (nuevo flujo) tras confirmar.
    if (!id && compraConfirmada && navType === 'back_forward') {
      if (!incomingAccessToken || !storedToken || incomingAccessToken !== storedToken) {
        navigate('/supervisora/compras', { replace: true, state: { aviso: 'No puedes reabrir una compra ya confirmada usando el botón atrás.' } });
      }
    }
  // Solo se debe recalcular cuando cambia id_piso o el token entrante
  }, [id_piso, incomingAccessToken, navigate, id]);

  // productosSeleccionados calculado a partir de productos y carrito
  const productosSeleccionados = useMemo(() => {
    const seleccionados = [];
    productos.forEach((cat) => {
      cat.subcategorias.forEach((sub) => {
        sub.productos.forEach((p) => {
          const cantidad = carrito[p.id] || 0;
          if (cantidad > 0) {
            seleccionados.push({ id: p.id, nombre: p.nombre, precio: p.precio, cantidad });
          }
        });
      });
    });
    return seleccionados;
  }, [productos, carrito]);

  // mantener ref actualizado para cleanup
  useEffect(() => {
    productosSeleccionadosRef.current = productosSeleccionados;
  }, [productosSeleccionados]);

  // Hydratación: cargar desde localStorage SOLO cuando 'productos' esté listo
  useEffect(() => {
    if (!id_piso || !productos.length) return;
    if (desdeModificar) { setHidratado(true); hydratedRef.current = true; return; }
    // Si venimos de "Sustituir" con la lista real y actual de la compra, no la
    // pisamos con un borrador viejo del piso (podría ser de OTRA compra anterior
    // del mismo piso, ya que el borrador se guarda solo por id_piso).
    if (origen === "DetalleCompra" && Array.isArray(productosPrevios) && productosPrevios.length > 0) {
      setHidratado(true); hydratedRef.current = true; return;
    }
    const key = `compraData_piso_${id_piso}`;
    const guardado = localStorage.getItem(key);
    if (guardado) {
      try {
        const data = JSON.parse(guardado);
        if (data.fecha !== todayISO()) {
          // Borrador de otro día: es de una compra anterior, no de la actual. Se
          // descarta y se elimina para no volver a resucitarlo ni dejarlo como basura.
          localStorage.removeItem(key);
        } else {
          const carritoInicial = {};
          (data.productos || []).forEach((p) => { carritoInicial[p.id] = p.cantidad; });
          setCarrito(carritoInicial);
          setAbreviaturaPiso(data.abreviatura_piso || data.abreviatura || "");
          setNotas(data.notas_supervisora || "");
        }
      } catch (e) {
        console.error('compraData no parseable', e);
      }
    }
    setHidratado(true);
    hydratedRef.current = true;
  }, [id_piso, productos, desdeModificar, origen, productosPrevios]);

  // Guardado automático solo después de hidratación
  useEffect(() => {
    // Modificar: borrador temporal (sessionStorage) para no perder los cambios con "atrás"
    if (desdeModificar) {
      // solo si ha cambiado algo (o ya había cambios pendientes): abrir una compra y no
      // tocarla no deja nada "sin confirmar"
      if (hidratado && (tocadoRef.current || leerBorradorModificar(id))) {
        guardarBorradorModificar(id, { productos: productosSeleccionados, notas_supervisora: notas, id_piso, abreviatura: abreviatura || abreviatura_piso });
      }
      return;
    }
    if (origen === "DetalleCompra") return; // Sustituir no usa localStorage: evita ensuciar futuros pedidos del mismo piso
    if (!hidratado || !id_piso) return;
  const precio_total = calcularPrecioTotalRedondeado(productosSeleccionados);
  const compraData = { id, productos: productosSeleccionados, id_piso, abreviatura_piso: abreviatura, user_supervisora, notas_supervisora: notas, precio_total, fecha: todayISO() };
    try { localStorage.setItem(`compraData_piso_${id_piso}`, JSON.stringify(compraData)); }
    catch (e) { console.error('No se pudo guardar compraData en localStorage:', e); }
  }, [productosSeleccionados, notas, id, id_piso, abreviatura_piso, user_supervisora, hidratado, desdeModificar, origen]);

  // Guardar al desmontar usando ref
  useEffect(() => {
    return () => {
      if (desdeModificar) return; // modificar no guarda (ni borra) el borrador de compra nueva
      if (origen === "DetalleCompra") return; // Sustituir no usa localStorage
      if (!hydratedRef.current || !id_piso) return;
      const lastProductos = productosSeleccionadosRef.current || [];
  const precio_total = calcularPrecioTotalRedondeado(lastProductos);
  const compraData = { id, productos: lastProductos, id_piso, abreviatura_piso:abreviatura, user_supervisora, notas_supervisora: notas, precio_total, fecha: todayISO() };
      try { localStorage.setItem(`compraData_piso_${id_piso}`, JSON.stringify(compraData)); }
      catch (e) { console.error('No se pudo guardar compraData en localStorage al desmontar:', e); }
    };
  }, [id_piso, desdeModificar, origen]);

  useEffect(() => setModoNoHay(!!modoNoHayState), [modoNoHayState]);

  useEffect(() => { fetchProductos(); }, []);

  // Si venimos con productosPrevios (por ejemplo al volver desde Carrito con navigate)
  useEffect(() => {
    // Modificar y se vuelve con "atrás" (p. ej. desde el carrito): lo último editado
    const borrador = desdeModificar ? leerBorradorModificar(id) : null;
    if (borrador && Array.isArray(borrador.productos)) {
      const carritoInicial = {};
      borrador.productos.forEach((p) => { if (p.cantidad > 0) carritoInicial[p.id] = p.cantidad; });
      setCarrito(carritoInicial);
      setNotas(borrador.notas_supervisora || "");
      if (abreviatura) setAbreviaturaPiso(abreviatura);
      return;
    }
    if (productosPrevios && Array.isArray(productosPrevios) && productosPrevios.length > 0) {
      const carritoInicial = {};
      productosPrevios.forEach((p) => { carritoInicial[p.id] = p.cantidad; });
      setCarrito(carritoInicial);
    } else {
      const storageKey = `compraData_piso_${id_piso}`;
      const guardado = localStorage.getItem(storageKey);
      if (guardado) {
        try {
          const data = JSON.parse(guardado);
          if (data.fecha !== todayISO()) {
            localStorage.removeItem(storageKey);
          } else {
            const carritoInicial = {};
            (data.productos || []).forEach((p) => { carritoInicial[p.id] = p.cantidad; });
            setCarrito(carritoInicial);
            setAbreviaturaPiso(data.abreviatura || data.abreviatura_piso || abreviatura || "");
            setNotas(data.notas_supervisora || "");
          }
        } catch (e) { console.error('compraData no parseable', e); }
      }
    }
    if (abreviatura) setAbreviaturaPiso(abreviatura);
    if (notas_supervisora) setNotas(notas_supervisora);
  }, [productosPrevios, abreviatura, notas_supervisora, user_supervisora, id_piso]);

  const handleClickCarrito = () => {
    // Hasta que el catálogo y el borrador estén cargados, productosSeleccionados sale
    // vacío: ir al carrito (y guardarlo) ahora machacaría el borrador con un carrito vacío.
    if (!hidratado) return;
    const precio_total = calcularPrecioTotalRedondeado(productosSeleccionados);
    const compraData = { id, productos: productosSeleccionados, id_piso, abreviatura_piso: abreviatura || abreviatura_piso, user_supervisora, notas_supervisora: notas, precio_total, fecha: todayISO() };
    if (!desdeModificar && origen !== "DetalleCompra") {
      try { localStorage.setItem(`compraData_piso_${id_piso}`, JSON.stringify(compraData)); } catch (e) { console.error('No se pudo guardar compraData en localStorage:', e); }
    }
    navigate(`/supervisora/carrito/${id_piso}`, { state: { ...compraData, accessToken, desdeModificar } });
  };

  const fetchProductos = async () => {
    try {
      const res = await axios.get(`/api/productos`);
      const nrm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const transformed = (Array.isArray(res.data) ? res.data : []).map((cat) => ({
        ...cat,
        subcategorias: (cat.subcategorias || []).map((sub) => ({
          ...sub,
          productos: (sub.productos || []).map((p) => ({
            ...p,
            _tokens: nrm(p.nombre).split(" ").filter(Boolean)
          }))
        }))
      }));
      setProductos(transformed);
    }
    catch (err) { setError("❌ Error al cargar productos."); }
    finally { setLoading(false); }
  };

  const aumentarCantidad = useCallback((productoId) => {
    tocadoRef.current = true;
    setCarrito((prev) => ({ ...prev, [productoId]: (prev[productoId] || 0) + 1 }));
  }, []);
  const disminuirCantidad = useCallback((productoId) => {
    tocadoRef.current = true;
    setCarrito((prev) => {
      const prevCantidad = prev[productoId] || 0;
      if (prevCantidad <= 1) { const newCarrito = { ...prev }; delete newCarrito[productoId]; return newCarrito; }
      return { ...prev, [productoId]: prevCantidad - 1 };
    });
  }, []);

  // const toNum = (v) => {
  //   if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  //   if (v == null) return 0;
  //   // Normaliza coma a punto antes de parsear
  //   const n = parseFloat(String(v).replace(',', '.'));
  //   return Number.isFinite(n) ? n : 0;
  // };

  // const calcularTotal = () => { 
  //   let total = 0; 
  //   productos.forEach((cat) => 
  //     cat.subcategorias.forEach((sub) => 
  //       sub.productos.forEach((p) => { 
  //         const cantidad = toNum(carrito[p.id]); 
  //         total += cantidad * toNum(p.precio); 
  //       }) 
  //     ) 
  //   ); 
  //   return total.toFixed(2); 
  // };
  
  const calcularTotal = useMemo(() => {
    let total = 0;
    productos.forEach((cat) =>
      cat.subcategorias.forEach((sub) =>
        sub.productos.forEach((p) => {
          total += (carrito[p.id] || 0) * p.precio;
        })
      )
    );
    return total.toFixed(2);
  }, [productos, carrito]);

  // Helper para precio_total con redondeo consistente (Number con 2 decimales)
  const calcularPrecioTotalRedondeado = (lista) => {
    const bruto = (lista || []).reduce((ac, prod) => ac + (Number(prod.precio) || 0) * (Number(prod.cantidad) || 0), 0);
    return Number(bruto.toFixed(2));
  };

  const seleccionarProducto = async (producto) => {
    const unidadesStr = window.prompt("Introduce el número de unidades (solo números):");
    if (unidadesStr === null) return;
    if (!/^\d+$/.test(unidadesStr)) { alert("Por favor, introduce un número entero válido mayor que 0."); return; }
    const unidades = parseInt(unidadesStr, 10);
    if (unidades <= 0) { alert("Por favor, introduce un número mayor que 0."); return; }
    const productoNuevo = { id: producto.id, nombre: producto.nombre, cantidad: unidades, precio: producto.precio, unidades };

    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("productos", JSON.stringify([productoSeleccionado, productoNuevo]));
      await axios.post(`/api/compras/cambios_compras_bbdd`, formData);
      alert("✅ Producto cambiado correctamente.");
      setCarrito((prev) => ({ ...prev, [producto.id]: unidades }));
  const nuevaListaProductos = [...productosSeleccionados.filter(p => p.id !== productoSeleccionado?.id), productoNuevo];
  const precio_total = calcularPrecioTotalRedondeado(nuevaListaProductos);
  navigate('/comprador/detalleCompra', { state: { ...location.state, productos: nuevaListaProductos, precio_total } });
    } catch (error) { console.error(error); alert("Error al actualizar el producto. Intenta de nuevo."); }
  };

  const handleFormChange = (e) => { const { name, value, files } = e.target; if (name === "cantidad" || name === "precio") { if (!/^\d*\.?\d*$/.test(value)) return; } if (name === "unidades") { if (!/^\d*$/.test(value)) return; } if (name === "imagen" && files && files.length > 0) setFormCambio(prev => ({ ...prev, imagen: files[0] })); else setFormCambio(prev => ({ ...prev, [name]: value })); };

  // ── Modificar compras: "atrás" (botón, móvil o navegador) pide confirmación ──
  // Se añade una entrada "de guarda" (misma pantalla) encima de la real: el "atrás"
  // del móvil/navegador quita esa guarda y aquí se muestra el aviso rojo.
  const [avisoSalirModificar, setAvisoSalirModificar] = useState(false);
  const saliendoModificarRef = useRef(false);
  const ponerGuardaModificar = () => {
    const st = window.history.state || {};
    const idx = (typeof st.idx === 'number' ? st.idx : 0) + 1;
    window.history.pushState({ ...st, idx, guardModificar: true }, '', window.location.href);
    registrarEntradaHistorial(idx, window.location.pathname, 'PUSH');
  };
  useEffect(() => {
    if (!desdeModificar || finAlEntrar.current) return undefined;
    const ruta = window.location.pathname;
    if (!(window.history.state || {}).guardModificar) ponerGuardaModificar();
    const onPop = () => {
      if (window.location.pathname !== ruta) return; // ya se ha salido de esta pantalla
      if (saliendoModificarRef.current) { window.history.back(); return; } // seguir saliendo
      if (!(window.history.state || {}).guardModificar) {
        // "atrás": con cambios sin confirmar se pregunta; sin cambios, se sale sin más
        if (leerBorradorModificar(id)) setAvisoSalirModificar(true);
        else { saliendoModificarRef.current = true; window.history.back(); }
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const seguirEditandoModificar = () => {
    setAvisoSalirModificar(false);
    if (!(window.history.state || {}).guardModificar) ponerGuardaModificar();
  };
  const salirSinGuardarModificar = (descartar = true) => {
    setAvisoSalirModificar(false);
    saliendoModificarRef.current = true;
    if (descartar) borrarBorradorModificar(id); // si no, quedan pendientes (en rojo en la lista)
    const ruta = window.location.pathname;
    window.history.back();
    // si no hay a dónde volver (p. ej. se reabrió la app aquí), directo a Modificar compras
    setTimeout(() => {
      if (window.location.pathname === ruta) navigate("/supervisora/modificarVerCompras", { state: { modo: "modificar" }, replace: true });
    }, 1500);
  };

  const volverAtras = () => {
    if (desdeModificar && !modoNoHay) {
      if (leerBorradorModificar(id)) setAvisoSalirModificar(true); // cambios sin confirmar: pregunta
      else salirSinGuardarModificar(false); // sin cambios: se sale sin más
      return;
    }
    // (modificar no usa el borrador compraData_piso_*: es el de una compra NUEVA de ese
    // piso, que puede estar a medias; no se borra al salir de modificar)
    if (!desdeModificar && origen !== "DetalleCompra" && hydratedRef.current) {
      // solo si ya se había cargado: antes de eso productosSeleccionados está vacío
      const precio_total = calcularPrecioTotalRedondeado(productosSeleccionados);
      const compraData = { id, productos: productosSeleccionados, id_piso, abreviatura_piso: abreviatura || abreviatura_piso, user_supervisora, notas_supervisora: notas, precio_total, fecha: todayISO() };
      try { localStorage.setItem(`compraData_piso_${id_piso}`, JSON.stringify(compraData)); } catch (e) { console.error('No se pudo guardar compraData en localStorage:', e); }
    }
    if (modoNoHay) {
      const precio_total = calcularPrecioTotalRedondeado(productosSeleccionados);
      navigate('/comprador/detalleCompra', { state: { ...location.state, productos: productosSeleccionados, precio_total }, replace: true });
      return;
    }
    if (desdeModificar) navigate("/supervisora/modificarVerCompras", { state: { modo: "modificar" }, replace: true });
    else if (origen === "PisoCrearCompras") navigate("/supervisora/pisoCrearCompras", { replace: true }); else navigate("/supervisora/compras", { replace: true });
  };

  const handleAceptarCambio = async () => {
    const { nombre, cantidad, precio, unidades, imagen } = formCambio;
    if (!nombre || !cantidad || !precio || !unidades || isNaN(Number(precio)) || isNaN(Number(unidades)) || Number(precio) < 0 || Number(unidades) <= 0) { alert("Por favor, completa todos los campos obligatorios con valores válidos."); return; }
    if (!window.confirm("¿Confirmas cambiar por otro producto con esos datos?")) return;
    try {
      const formData = new FormData();
      formData.append("nombre", nombre); formData.append("cantidad", cantidad); formData.append("precio", Number(precio)); formData.append("unidades", Number(unidades)); formData.append("productoSeleccionado", JSON.stringify(productoSeleccionado)); formData.append("id", id);
      if (imagen && typeof imagen !== "string") formData.append("imagen", imagen);
      const res = await axios.post(`/api/compras/cambios_compras`, formData);
      const returnedFilename = res.data?.filename ?? null;
      const nuevaListaProductos = (location.state.productos || []).filter((p) => !(p.id === productoSeleccionado?.id && p.nombre === productoSeleccionado?.nombre && p.cantidad === productoSeleccionado?.cantidad && p.precio === productoSeleccionado?.precio));
      nuevaListaProductos.push({ id: null, nombre, cantidad: Number(unidades), precio: Number(precio), imagen: returnedFilename });
  const precio_total = calcularPrecioTotalRedondeado(nuevaListaProductos);
      alert("✅ Producto cambiado y guardado correctamente.");
      setMostrarFormularioCambio(false); setFormCambio({ nombre: "", cantidad: "", precio: "", unidades: "", imagen: null });
      navigate('/comprador/detalleCompra', { state: { ...location.state, productos: nuevaListaProductos, precio_total } });
    } catch (error) { alert("Error al guardar el producto. Intenta de nuevo."); console.error(error); }
  };

  return (
    <>
      {/* ESTILOS SIN CAMBIOS */}
      <style>{`
        /* Evitar scroll horizontal accidental y asegurar que los elementos quepan en el panel */
        html, body, #root {
          overflow-x: hidden;
        }
        /* Asegurar box-sizing consistente dentro del panel para evitar desbordados por padding */
        .admin-gestion-productos-container, .admin-gestion-productos-container * {
          box-sizing: border-box;
        }
        /* Contenedor principal */
        .admin-gestion-productos-container {
          position: relative;
          min-height: 100vh;
          padding: 20px;
          max-width: 100%;
          overflow-x: hidden;
        }
        /* Categorías y subcategorías estilos base */
        .category {
          margin-bottom: 30px;
          border-top: 2px solid #ccc;
          border-bottom: 2px solid #ccc;
          padding: 20px;
          background-color: #d9fdd3;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          text-align: center;
        }
        .category summary {
          font-size: 30px;
          font-weight: bold;
          cursor: pointer;
          margin-bottom: 15px;
          text-align: center;
        }
        .subcategory {
          margin: 20px 0;
          padding: 20px;
          background-color: #eefdea;
          border-top: 1px solid #bbb;
          border-bottom: 1px solid #bbb;
          box-shadow: inset 0 1px 2px rgba(0,0,0,0.03);
          text-align: center;
        }
        .subcategory summary {
          font-size: 26px;
          font-weight: bold;
          margin-bottom: 15px;
          cursor: pointer;
        }
        /* Producto base */
        .product {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          margin: 20px 0;
          padding: 15px;
          border-bottom: 1px solid #ddd;
          text-align: center;
        }
        .producto-imagen {
          width: 150px;
          max-width: 150px;
          height: auto;
          object-fit: cover;
          border-radius: 10px;
          box-shadow: 0 3px 6px rgba(0,0,0,0.2);
          margin-right: 20px;
          flex-shrink: 0;
        }
        .producto-info {
          flex: 1;
          text-align: left;
          font-size: 18px;
        }
        .cantidadControls {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 120px;
          justify-content: center;
          margin-left: 20px;
        }
        .cantidadBtn {
          padding: 6px 12px;
          font-size: 20px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          user-select: none;
        }
        .cantidadNum {
          font-size: 18px;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
        }
        .price {
          font-weight: bold;
          margin-top: 8px;
        }
        .precioKg {
          font-size: 14px;
          color: #666;
          margin-left: 5px;
        }
        .producto-blocked {
          opacity: 0.6;
          filter: grayscale(60%);
          background-color: #fafafa;
        }
        .producto-blocked .cantidadBtn {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .blocked-badge {
          background: #ffdddd;
          color: #800;
          border: 1px solid #f55;
          padding: 10px 14px;
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
          font-weight: 800;
          font-size: 15px;
          text-align: center;
          white-space: nowrap;
        }
        /* Botón carrito fijo */
        .boton-carrito {
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 1000;
          padding: 10px 20px;
          background-color: #007bff;
          color: "white";
          border: "none";
          borderRadius: "5px";
          cursor: "pointer";
          box-shadow: "0 2px 5px rgba(0,0,0,0.2)";
          font-weight: "bold";
          font-size: "20px";
        }
        /* Buscador */
        .buscador-wrapper {
          position: relative;
          width: 100%;
          max-width: 480px; /* ocupa todo el ancho disponible hasta 480px */
          margin: 0 auto 25px auto;
          padding: 0 16px; /* evitar que el input toque los bordes en pantallas pequeñas */
          box-sizing: border-box;
        }
        .buscador-input {
          width: 100%;
          padding: 12px 46px 12px 18px; /* espacio para la X */
          font-size: 20px;
          border: 1px solid #ccc;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
          transition: box-shadow .2s, border-color .2s;
        }
        .buscador-input:focus {
          outline: none;
          border-color: #4094ff;
          box-shadow: 0 0 0 3px rgba(64,148,255,0.25);
        }
        .buscador-clear-btn {
          position: absolute;
          top: 50%;
          right: 24px;
          transform: translateY(-50%);
          background: linear-gradient(135deg,#ff6a6a,#d93636);
          color: #fff;
          border: none;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 20px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: transform .18s, box-shadow .18s;
        }
        .buscador-clear-btn.disabled {
          opacity: 0.35;
          cursor: not-allowed;
          background: linear-gradient(135deg,#b5b5b5,#9e9e9e);
          box-shadow: none;
        }
        .buscador-clear-btn:hover {
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 3px 10px rgba(0,0,0,0.4);
        }
        .buscador-clear-btn:active {
          transform: translateY(-50%) scale(.94);
        }
        @media (max-width: 768px) {
          .buscador-wrapper { width: 95%; }
          .buscador-input { font-size: 18px; padding: 12px 44px 12px 16px; }
          .buscador-clear-btn { width: 32px; height: 32px; font-size: 18px; }
        }

        /* --- ESTILOS MÓVIL --- */
        @media (max-width: 768px) {
          .admin-gestion-productos-container {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
          }
          .category, .subcategory {
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            width: 100% !important;
            text-align: left;
          }
          /* Producto layout móvil */

          .producto-imagen {
            grid-column: 1 / 2;
            grid-row: 1 / 3; /* Ocupa dos filas */
            width: 110px;
            height: 110px;
            margin-right: 0;
          }
          .producto-info {
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            font-size: 16px;
            text-align: left;
          }
          .cantidadControls {
            grid-column: 2 / 3;
            grid-row: 2 / 3;
            display: flex !important;
            justify-content: flex-start !important;
            gap: 10px;
            margin-left: 0;
            min-width: auto;
          }
        }
      `}</style>

      <div className="admin-gestion-productos-container">
        <h2
          style={{
            fontSize: "32px",
            marginBottom: "30px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Gestión de Productos
        </h2>

        {desdeModificar && recuperadoModificar && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 auto 12px", maxWidth: 560, background: "#fff1f2", border: "1.5px solid #fecdd3", borderLeft: "6px solid #e11d48", borderRadius: 12, padding: "7px 10px", fontSize: 13.5, color: "#9f1239" }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#e11d48", flexShrink: 0 }} />
            <span style={{ flex: 1, lineHeight: 1.3, textAlign: "left" }}>
              <b>Cambios sin confirmar recuperados</b>{recuperadoModificar.ts ? ` (${new Date(recuperadoModificar.ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })})` : ""}
              <span style={{ color: "#be123c" }}> · confírmalos en el carrito</span>
            </span>
            <button
              onClick={() => {
                if (!window.confirm("¿Descartar los cambios sin confirmar y volver a la compra como está guardada?")) return;
                borrarBorradorModificar(id);
                tocadoRef.current = false;
                const original = {};
                (productosPrevios || []).forEach((p) => { if (p.cantidad > 0) original[p.id] = p.cantidad; });
                setCarrito(original);
                setNotas(notas_supervisora || "");
                setRecuperadoModificar(null);
              }}
              style={{ border: "1px solid #fda4af", background: "#fff", color: "#be123c", borderRadius: 999, padding: "4px 10px", fontWeight: 800, fontSize: 12.5, cursor: "pointer", flexShrink: 0 }}
            >
              Descartar
            </button>
          </div>
        )}

        {avisoSalirModificar && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 5000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: "#fff", borderRadius: 18, width: "min(420px, 100%)", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}>
              <div style={{ background: "#dc2626", color: "#fff", padding: "16px 18px", fontSize: 20, fontWeight: 900 }}>⚠️ Cambios sin confirmar</div>
              <div style={{ padding: "16px 18px", fontSize: 16, color: "#1f2937", lineHeight: 1.5 }}>
                Tus cambios <b>todavía NO se han aplicado al pedido</b>. Para aplicarlos, ve al carrito y confirma la compra.
                <br />Si sales, quedarán <b>pendientes</b> (los verás en rojo en «Modificar compras»), o puedes descartarlos.
              </div>
              <div style={{ display: "flex", gap: 10, padding: "0 18px 18px", flexWrap: "wrap" }}>
                <button onClick={seguirEditandoModificar} style={{ flex: "1 1 150px", padding: "14px 10px", borderRadius: 12, border: "none", background: "#16a34a", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer" }}>
                  Seguir editando
                </button>
                <button onClick={() => salirSinGuardarModificar(false)} style={{ flex: "1 1 150px", padding: "14px 10px", borderRadius: 12, border: "none", background: "#f59e0b", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer" }}>
                  Salir (quedan pendientes)
                </button>
                <button onClick={() => salirSinGuardarModificar(true)} style={{ flex: "1 1 150px", padding: "14px 10px", borderRadius: 12, border: "none", background: "#dc2626", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer" }}>
                  Descartar cambios
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={volverAtras}
          style={{
            padding: "16px 24px",
            borderRadius: 6,
            backgroundColor: "#e46d6dff",
            border: "1px solid #ccc",
            cursor: "pointer",
            fontWeight: 600,
            fontSize:20,
            position: "fixed",      // lo hace fijo
            bottom: "calc(20px + var(--fichaje-h, 0px))",   // separación desde abajo (y por encima de la barra de fichaje)
            left: "20px",  
          }}
        >
          ← Atrás
        </button>

        {!modoNoHay && (
          <button
            className="boton-carrito"
            onClick={handleClickCarrito}
            disabled={anyBlocked || !hidratado}
            aria-disabled={anyBlocked || !hidratado}
            style={{
              position: "fixed",
              top: "20px",       // separación desde arriba
              left: "20px",      // separación desde la izquierda
              padding: "16px 24px",  // más grande
              fontSize: "22px",       // texto más grande
              backgroundColor: anyBlocked || !hidratado ? "#9bb3df" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: anyBlocked ? "not-allowed" : "pointer",
              fontWeight: "bold",
              boxShadow: anyBlocked ? "none" : "0 4px 10px rgba(0,0,0,0.3)",
              zIndex: 1000,
              transition: "transform 0.2s, box-shadow 0.2s",
              opacity: anyBlocked ? 0.7 : 1,
            }}
            onMouseEnter={e => { if (!anyBlocked) { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 6px 14px rgba(0,0,0,0.35)"; } }}
            onMouseLeave={e => { if (!anyBlocked) { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.3)"; } }}
            title={anyBlocked ? 'Hay productos bloqueados (viniendo de DetalleCompra). No se puede abrir el carrito.' : 'Ir al carrito'}
          >
            {hidratado ? <>🛒 Total: {calcularTotal} €</> : "⏳ Cargando…"}
          </button>
        )}

        <h2 style={{ marginBottom: "10px", textAlign: "center" }}>
          Realizando compra para <strong>{abreviatura}</strong>
        </h2>

        <div className="buscador-wrapper">
          <input
            className="buscador-input"
            type="text"
            placeholder="Buscar producto por nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar productos"
          />
          <button
            type="button"
            className={`buscador-clear-btn ${busqueda ? '' : 'disabled'}`}
            aria-label={busqueda ? 'Limpiar búsqueda' : 'Sin texto para limpiar'}
            onClick={() => {
              if (!busqueda) return;
              setBusqueda("");
              setOpenCategoria(null);
              setOpenSubcategoria(null);
            }}
            title={busqueda ? 'Limpiar búsqueda' : 'No hay texto'}
            disabled={!busqueda}
          >
            ×
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", marginTop: "30px" }}>⏳ Cargando productos...</p>
        ) : error ? (
          <p style={{ color: "red", textAlign: "center" }}>{error}</p>
        ) : productosFiltrados.length === 0 ? (
          <p style={{ textAlign: "center", marginTop: "20px" }}>
            No se encontraron productos que coincidan con la búsqueda.
          </p>
        ) : (
          productosFiltrados
            .map((cat) => (
              <Categoria
                key={cat.categoria}
                cat={cat}
                isPremium={cat.categoria === "Premium"}
                openCategoria={openCategoria}
                setOpenCategoria={setOpenCategoria}
                openSubcategoria={openSubcategoria}
                setOpenSubcategoria={setOpenSubcategoria}
                carrito={carrito}
                modoNoHay={modoNoHay}
                seleccionarProducto={seleccionarProducto}
                aumentarCantidad={aumentarCantidad}
                disminuirCantidad={disminuirCantidad}
                busquedaFiltrada={deferredBusqueda}
                subcategoryRefs={subcategoryRefs}
                blockedIds={blockedIds}
              />
            ))
        )}

        {modoNoHay && (
          <>
            <button
              style={{
                position: "fixed",
                top: "20px",
                left: "20px",
                padding: "16px 22px",
                backgroundColor: "#117ac0ff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: anyBlocked ? 'not-allowed' : 'pointer',
                fontWeight: "bold",
                fontSize: 17,
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                zIndex: 1100,
              }}
              onClick={() => { if (!anyBlocked) setMostrarFormularioCambio(true); }}
              disabled={anyBlocked}
              aria-disabled={anyBlocked}
            >
              Cambiar por 
              <br />
              otro producto
            </button>

            {/* Formulario modal */}
            {mostrarFormularioCambio && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1200,
                }}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    width: "90%",
                    maxWidth: "400px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <h3 style={{ marginBottom: "15px" }}>
                    Cambiar por otro producto
                  </h3>
                  <label>
                    Nombre:<br />
                    <input
                      type="text"
                      name="nombre"
                      value={formCambio.nombre}
                      onChange={handleFormChange}
                      style={{ width: "100%", marginBottom: "10px" }}
                    />
                  </label>
                  <label>
                    Cantidad (kg o L):<br />
                    <input
                      type="text"
                      name="cantidad"
                      value={formCambio.cantidad}
                      onChange={handleFormChange}
                      style={{ width: "100%", marginBottom: "10px" }}
                    />
                  </label>
                  <label>
                    Precio:<br />
                    <input
                      type="number"
                      name="precio"
                      value={formCambio.precio}
                      onChange={handleFormChange}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value || 0);
                        setFormCambio((prev) => ({
                          ...prev,
                          precio: value.toFixed(2),
                        }));
                      }}
                      style={{ width: "100%", marginBottom: "10px" }}
                      step="0.01"
                      min="0"
                    />
                  </label>
                  <label>
                    Unidades:<br />
                    <input
                      type="number"
                      name="unidades"
                      value={formCambio.unidades}
                      onChange={handleFormChange}
                      style={{ width: "100%", marginBottom: "10px" }}
                      min="1"
                    />
                  </label>
                  <label>
                    Imagen (foto):
                    <input
                      type="file"
                      name="imagen"
                      accept="image/*"
                      onChange={handleFormChange}
                      style={{ width: "100%", marginBottom: "10px" }}
                    />
                  </label>
                  <div style={{ textAlign: "right" }}>
                    <button
                      onClick={() => setMostrarFormularioCambio(false)}
                      style={{
                        marginRight: "10px",
                        padding: "6px 12px",
                        cursor: "pointer",
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleAceptarCambio}
                      style={{
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Aceptar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Flecha hacia arriba */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            position: "fixed",
            bottom: "calc(20px + var(--fichaje-h, 0px))",
            right: "20px",
            backgroundColor: "#eb9310ff",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "56px",
            height: "56px",
            fontSize: "32px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            cursor: "pointer",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          title="Ir arriba"
        >
          ↑
        </button>
      </div>
    </>
  );
};

export default CrearCompras;
